<template>
  <div class="print-page-wrap">

    <!-- Screen toolbar (hidden when printing) -->
    <div class="print-toolbar no-print">
      <button class="toolbar-btn" @click="$router.go(-1)">
        <font-awesome-icon icon="arrow-left" class="me-1" /> Back
      </button>
      <div class="toolbar-right">
        <button class="toolbar-btn toolbar-btn--primary" @click="printDoc">
          <font-awesome-icon icon="print" class="me-1" /> Print
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="print-loading no-print">
      <div class="spinner-border text-primary" role="status"></div>
      <p>Loading requisition…</p>
    </div>

    <!-- Document -->
    <div v-else-if="pr" class="document" id="printMe">

      <!-- Document header -->
      <div class="doc-header">
        <div class="doc-brand">
          <img v-if="business.logo" :src="hostName + business.logo" class="doc-logo" alt="Logo" />
          <div v-else class="doc-logo-placeholder">{{ (business.name || '?').charAt(0) }}</div>
          <div class="doc-brand-text">
            <div class="doc-company-name">{{ business.name || '—' }}</div>
            <div class="doc-company-sub" v-if="business.address">{{ business.address }}</div>
          </div>
        </div>
        <div class="doc-title-block">
          <div class="doc-title">PURCHASE REQUISITION</div>
          <table class="doc-meta-table">
            <tr>
              <td class="dmt-label">PR Number</td>
              <td class="dmt-value mono">{{ pr.pr_number }}</td>
            </tr>
            <tr>
              <td class="dmt-label">Date</td>
              <td class="dmt-value">{{ formatDate(pr.created_at) }}</td>
            </tr>
            <tr>
              <td class="dmt-label">Status</td>
              <td class="dmt-value">
                <span :class="['status-pill', `status-pill--${pr.status}`]">{{ formatStatus(pr.status) }}</span>
              </td>
            </tr>
            <tr v-if="pr.need_by_date">
              <td class="dmt-label">Need By</td>
              <td class="dmt-value" :class="isOverdue(pr.need_by_date) ? 'text-danger' : ''">{{ formatDate(pr.need_by_date) }}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="doc-divider"></div>

      <!-- Requisition info grid -->
      <div class="doc-info-grid">
        <div class="doc-info-item">
          <span class="dii-label">Requester</span>
          <span class="dii-value">{{ requesterName }}</span>
        </div>
        <div class="doc-info-item">
          <span class="dii-label">Category</span>
          <span class="dii-value">{{ pr.category?.name || '—' }}</span>
        </div>
        <div class="doc-info-item">
          <span class="dii-label">Department</span>
          <span class="dii-value">{{ pr.department?.name || '—' }}</span>
        </div>
        <div class="doc-info-item">
          <span class="dii-label">Project</span>
          <span class="dii-value">{{ pr.project ? pr.project.project_code + ' — ' + pr.project.project_name : '—' }}</span>
        </div>
        <div class="doc-info-item">
          <span class="dii-label">Cost Code</span>
          <span class="dii-value">{{ pr.cost_code || '—' }}</span>
        </div>
        <div class="doc-info-item">
          <span class="dii-label">Currency</span>
          <span class="dii-value">{{ pr.currency || '—' }}</span>
        </div>
        <div class="doc-info-item">
          <span class="dii-label">Urgency</span>
          <span class="dii-value" style="text-transform:capitalize">{{ pr.urgency || '—' }}</span>
        </div>
      </div>

      <div class="doc-divider doc-divider--dashed"></div>

      <!-- Title -->
      <div class="doc-section-label">{{ pr.title }}</div>
      <div v-if="pr.description" class="doc-description">{{ pr.description }}</div>

      <!-- Line items -->
      <div class="table-section-head">
        <span class="table-section-title">Line Items</span>
        <span class="table-count-badge">{{ pr.lines?.length || 0 }} item{{ (pr.lines?.length || 0) !== 1 ? 's' : '' }}</span>
      </div>
      <div class="doc-table-wrap">
        <table class="doc-table">
          <colgroup>
            <col class="col-no" />
            <col class="col-desc" />
            <col class="col-cat" />
            <col class="col-qty" />
            <col class="col-uom" />
            <col class="col-cost" />
            <col class="col-total" />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Category</th>
              <th class="th-r">Qty</th>
              <th>UOM</th>
              <th class="th-r">Unit Cost</th>
              <th class="th-r">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, i) in pr.lines" :key="line.id" :class="i % 2 === 1 ? 'row-alt' : ''">
              <td class="td-idx">{{ i + 1 }}</td>
              <td class="td-desc-cell">
                <div class="td-desc">{{ line.description }}</div>
                <div v-if="line.specification" class="td-spec">{{ line.specification }}</div>
              </td>
              <td class="td-cat">{{ line.category?.name || '—' }}</td>
              <td class="td-r td-qty">{{ line.quantity }}</td>
              <td class="td-uom">{{ line.uom || '—' }}</td>
              <td class="td-r td-money">{{ formatCurrency(line.estimated_unit_cost, pr.currency) }}</td>
              <td class="td-r td-money td-total">{{ formatCurrency(line.estimated_total, pr.currency) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="tfoot-row">
              <td colspan="6" class="tfoot-label">Estimated Total</td>
              <td class="tfoot-value">{{ formatCurrency(pr.estimated_total, pr.currency) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Notes -->
      <div v-if="pr.notes" class="doc-notes-box">
        <div class="doc-notes-label">Notes</div>
        <div class="doc-notes-text">{{ pr.notes }}</div>
      </div>

      <!-- Return reason -->
      <div v-if="pr.return_reason" class="doc-return-box">
        <div class="doc-notes-label">Return Reason</div>
        <div class="doc-notes-text">{{ pr.return_reason }}</div>
      </div>

      <!-- Approval trail -->
      <template v-if="pr.approval_request?.steps?.length">
        <div class="doc-divider doc-divider--dashed"></div>
        <div class="doc-section-label">Approval Trail</div>
        <table class="doc-approval-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Approver / Position</th>
              <th>Status</th>
              <th>Date</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="step in pr.approval_request.steps" :key="step.id">
              <td>{{ step.step_order }}</td>
              <td>{{ step.position_name || '—' }}</td>
              <td style="text-transform:capitalize">{{ (step.status || '').replace('_', ' ') }}</td>
              <td>{{ step.acted_at ? formatDate(step.acted_at) : '—' }}</td>
              <td class="dim">{{ step.comments || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- Signature block -->
      <div class="doc-divider doc-divider--dashed"></div>
      <div class="doc-sig-grid">
        <div class="doc-sig-item">
          <div class="doc-sig-line"></div>
          <div class="doc-sig-label">Requested by</div>
          <div class="doc-sig-sub">{{ requesterName }}</div>
        </div>
        <div class="doc-sig-item doc-sig-item--approvers">
          <div class="doc-sig-line"></div>
          <div class="doc-sig-label">Reviewed &amp; Approved By</div>
          <template v-if="approversList.length">
            <div v-for="(a, i) in approversList" :key="i" class="doc-sig-approver">
              <span class="doc-sig-approver-name">{{ a.name }}</span>
              <span class="doc-sig-approver-date">{{ a.date }}</span>
            </div>
          </template>
          <div v-else class="doc-sig-sub">—</div>
        </div>
      </div>

      <!-- Footer -->
      <div class="doc-footer">
        Generated {{ formatDate(new Date().toISOString()) }} · {{ pr.pr_number }} · {{ business.name }}
      </div>

    </div>

    <div v-else class="print-loading no-print">
      <p>Requisition not found.</p>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import url from '../../../main';
import { useToast } from 'vue-toastification';

export default {
  name: 'PrintPR',
  setup() { return { toast: useToast() }; },

  data() {
    return {
      pr:       null,
      business: {},
      loading:  true,
      hostName: url.hostName,
    };
  },

  computed: {
    requesterName() {
      const r = this.pr?.requester ?? this.pr?.creator;
      if (!r) return '—';
      return [r.first_name, r.last_name].filter(Boolean).join(' ') || '—';
    },

    approversList() {
      const steps = this.pr?.approval_request?.steps ?? [];
      return steps
        .filter(s => s.acted_at)
        .map(s => {
          const emp = s.acted_by && typeof s.acted_by === 'object' ? s.acted_by : null;
          const name = emp
            ? [emp.first_name, emp.last_name].filter(Boolean).join(' ') || s.position_name || '—'
            : s.position_name || '—';
          return { name, date: this.formatDate(s.acted_at) };
        });
    },
  },

  mounted() {
    Promise.all([this.fetchPR(), this.fetchBusiness()])
      .finally(() => { this.loading = false; });
  },

  methods: {
    async fetchPR() {
      try {
        const { data } = await axios.get(`/procurement/purchase-requisitions/${this.$route.params.id}`);
        this.pr = data.data;
      } catch {
        this.toast.error('Failed to load requisition');
      }
    },

    async fetchBusiness() {
      try {
        const { data } = await axios.get('/admin/business/user/active-business');
        this.business = data.data ?? {};
      } catch { /* non-critical */ }
    },

    printDoc() {
      const style = document.createElement('style');
      style.id = '__pr-print-style';
      style.innerHTML = `
        @page { size: A4; margin: 14mm; }
        @media print {
          body > * { visibility: hidden !important; }
          #printMe, #printMe * { visibility: visible !important; }
          #printMe { position: absolute; left: 0; top: 0; width: 100%; background: white; }
        }
      `;
      document.head.appendChild(style);
      window.print();
      setTimeout(() => document.getElementById('__pr-print-style')?.remove(), 1200);
    },

    formatStatus(s) {
      const map = {
        draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review',
        returned: 'Returned', approved: 'Approved', sourcing: 'Sourcing',
        partially_sourced: 'Partially Sourced', fully_sourced: 'Fully Sourced',
        closed: 'Closed', cancelled: 'Cancelled',
      };
      return map[s] ?? s;
    },

    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatCurrency(amount, currency) {
      const sym = { NGN: '₦', GBP: '£', USD: '$', EUR: '€' }[currency] ?? (currency ?? '');
      return sym + Number(amount || 0).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    isOverdue(d) { return d && new Date(d) < new Date(); },
  },
};
</script>

<style scoped>
/* ── Page shell ─────────────────────────────────────────────────────────── */
.print-page-wrap { background: #f0f4f8; min-height: 100vh; padding: 20px; }

.print-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  max-width: 860px; margin: 0 auto 20px; gap: 10px;
}
.toolbar-right { display: flex; gap: 10px; }
.toolbar-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; color: #1a2b45; border: 1.5px solid #dde5f0;
  font-size: 13px; font-weight: 600; padding: 9px 18px; border-radius: 8px; cursor: pointer;
}
.toolbar-btn:hover { background: #f4f7fb; }
.toolbar-btn--primary { background: #004AAD; color: #fff; border-color: #004AAD; }
.toolbar-btn--primary:hover { background: #003a8c; }

.print-loading { max-width: 860px; margin: 60px auto; text-align: center; color: #7a90b0; display: flex; flex-direction: column; align-items: center; gap: 14px; }

/* ── Document ───────────────────────────────────────────────────────────── */
.document {
  max-width: 860px; margin: 0 auto;
  background: #fff; border-radius: 12px; padding: 48px 56px;
  box-shadow: 0 4px 24px rgba(0,0,0,.08);
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 13px; color: #1a2b45;
}

/* Header */
.doc-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.doc-brand  { display: flex; align-items: center; gap: 14px; }
.doc-logo   { height: 56px; width: auto; object-fit: contain; }
.doc-logo-placeholder {
  width: 56px; height: 56px; border-radius: 10px; background: #004AAD;
  color: #fff; font-size: 22px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.doc-company-name { font-size: 17px; font-weight: 700; color: #1a2b45; }
.doc-company-sub  { font-size: 12px; color: #7a90b0; margin-top: 2px; }

.doc-title-block { text-align: right; }
.doc-title {
  font-size: 22px; font-weight: 800; color: #004AAD;
  letter-spacing: .04em; text-transform: uppercase; margin-bottom: 12px;
}
.doc-meta-table { margin-left: auto; border-collapse: collapse; }
.dmt-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #7a90b0; padding: 3px 10px 3px 0; white-space: nowrap; }
.dmt-value { font-size: 13px; color: #1a2b45; font-weight: 600; padding: 3px 0; }

/* Dividers */
.doc-divider         { border: none; border-top: 2px solid #e4eaf2; margin: 24px 0; }
.doc-divider--dashed { border-top-style: dashed; border-color: #c8d5e8; }

/* Info grid */
.doc-info-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px 24px;
}
.doc-info-item { display: flex; flex-direction: column; gap: 3px; }
.dii-label { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #aab8cc; }
.dii-value { font-size: 13px; color: #1a2b45; font-weight: 600; }

/* Section label */
.doc-section-label { font-size: 14px; font-weight: 700; color: #1a2b45; margin-bottom: 6px; }
.doc-description   { font-size: 13px; color: #4b5c73; margin-bottom: 16px; }

/* Line items table */
.table-section-head {
  display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
}
.table-section-title {
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .07em; color: #1a2b45;
}
.table-count-badge {
  background: #eef2f7; color: #64748b; font-size: 11px; font-weight: 600;
  border-radius: 20px; padding: 2px 9px;
}

.doc-table-wrap {
  border: 1px solid #d5dfe9; border-radius: 10px; overflow: hidden;
}
.doc-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;   /* honour colgroup widths exactly */
  font-size: 12.5px;
}

/* Column widths — total = 100% */
.col-no    { width: 4%; }
.col-desc  { width: 33%; }
.col-cat   { width: 16%; }
.col-qty   { width: 7%; }
.col-uom   { width: 7%; }
.col-cost  { width: 15%; }
.col-total { width: 18%; }

/* Header */
.doc-table thead tr { background: #1a2b45; }
.doc-table th {
  padding: 11px 14px;
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .07em;
  color: rgba(255,255,255,.7);
  text-align: left; border: none;
  overflow: hidden; white-space: nowrap;
}
.th-r { text-align: right; }

/* Body rows */
.doc-table tbody tr           { background: #fff; }
.doc-table tbody tr.row-alt   { background: #f7f9fc; }
.doc-table tbody tr:not(:last-child) td { border-bottom: 1px solid #e8edf3; }
.doc-table td { padding: 11px 14px; vertical-align: middle; }

/* Individual cell styles */
.td-idx  {
  color: #b0bdd0; font-size: 11.5px; font-weight: 700;
  text-align: center;
}
.td-desc-cell { vertical-align: top; padding-top: 12px; }
.td-desc {
  font-weight: 600; color: #1a2b45; font-size: 13px;
  word-break: break-word; line-height: 1.4;
}
.td-spec {
  font-size: 11px; color: #8fa0b5; margin-top: 4px;
  line-height: 1.4; word-break: break-word;
}
.td-cat {
  color: #4b5c73; font-size: 12px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.td-r     { text-align: right; }
.td-qty   {
  font-size: 13px; font-weight: 600; color: #1a2b45;
  font-variant-numeric: tabular-nums;
}
.td-uom   {
  font-size: 11.5px; color: #8fa0b5;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.td-money {
  font-size: 12.5px; color: #1a2b45;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.td-total { font-weight: 700; font-size: 13px; color: #004AAD; }

/* Footer total row */
.doc-table tfoot .tfoot-row { background: #f0f5ff; }
.doc-table tfoot td         { border-top: 2px solid #d5dfe9; border-bottom: none; }
.tfoot-label {
  padding: 13px 14px;
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .07em;
  color: #64748b; text-align: right;
}
.tfoot-value {
  padding: 13px 14px;
  font-size: 15px; font-weight: 800;
  color: #004AAD; text-align: right;
  font-variant-numeric: tabular-nums; white-space: nowrap;
}

.fw6 { font-weight: 600; }
.dim { color: #7a90b0; }

/* Notes / return reason */
.doc-notes-box, .doc-return-box {
  border-radius: 8px; padding: 14px 16px; margin-top: 16px;
}
.doc-notes-box  { background: #f8fafc; border: 1px solid #e4eaf2; }
.doc-return-box { background: #fff7ed; border: 1px solid #fed7aa; }
.doc-notes-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #7a90b0; margin-bottom: 6px; }
.doc-notes-text  { font-size: 13px; color: #1a2b45; }

/* Approval trail table */
.doc-approval-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 12px; }
.doc-approval-table th {
  padding: 7px 10px; font-size: 10.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .05em; color: #7a90b0;
  background: #f8fafc; border-bottom: 1px solid #e4eaf2; text-align: left;
}
.doc-approval-table td { padding: 8px 10px; border-bottom: 1px solid #f0f4f8; }

/* Signature block */
.doc-sig-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 32px; margin-top: 8px; }
.doc-sig-line  { border-bottom: 1.5px solid #1a2b45; margin-bottom: 8px; height: 40px; }
.doc-sig-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #1a2b45; }
.doc-sig-sub   { font-size: 11px; color: #aab8cc; margin-top: 2px; }
.doc-sig-approver { display: flex; align-items: baseline; gap: 8px; margin-top: 4px; }
.doc-sig-approver-name { font-size: 12px; font-weight: 600; color: #1a2b45; }
.doc-sig-approver-date { font-size: 11px; color: #7a90b0; }

/* Status pills */
.status-pill { display: inline-block; font-size: 11px; font-weight: 600; border-radius: 20px; padding: 2px 10px; }
.status-pill--draft         { background: #f1f5f9; color: #64748b; }
.status-pill--submitted     { background: #eff6ff; color: #1d4ed8; }
.status-pill--under_review  { background: #fff7ed; color: #c2410c; }
.status-pill--returned      { background: #fef9c3; color: #854d0e; }
.status-pill--approved      { background: #dcfce7; color: #15803d; }
.status-pill--cancelled     { background: #fee2e2; color: #dc2626; }

.mono { font-family: monospace; }
.text-danger { color: #dc2626; }
.me-1 { margin-right: 4px; }

/* Footer */
.doc-footer { margin-top: 32px; text-align: center; font-size: 10.5px; color: #aab8cc; }

/* ── Print overrides ────────────────────────────────────────────────────── */
@media print {
  .no-print      { display: none !important; }
  .print-page-wrap { background: white; padding: 0; }
  .document      { box-shadow: none; border-radius: 0; padding: 0; max-width: 100%; }
}

@media (max-width: 700px) {
  .document         { padding: 24px 20px; }
  .doc-header       { flex-direction: column; }
  .doc-title-block  { text-align: left; }
  .doc-meta-table   { margin-left: 0; }
  .doc-info-grid    { grid-template-columns: 1fr 1fr; }
  .doc-sig-grid     { grid-template-columns: 1fr; }
  /* hide Category (col 3) and UOM (col 5) on small screens */
  .doc-table th:nth-child(3), .doc-table td:nth-child(3),
  .doc-table th:nth-child(5), .doc-table td:nth-child(5) { display: none; }
  .col-cat, .col-uom { display: none; }
  .col-desc { width: 50%; }
  .col-qty  { width: 12%; }
  .col-cost { width: 18%; }
  .col-total{ width: 20%; }
}
</style>
